// Genera e imprime un recibo de los servicios seleccionados de un cliente.
// El recibo no es factura (se factura por otro medio) y nunca lleva IVA. El numero lo asigna el
// sistema: iniciales del cliente + consecutivo (como las cotizaciones). Cada recibo queda guardado.
import { useState, useEffect } from 'react';
import API from '../api';
import html2canvas from 'html2canvas';
import { imprimirHTML } from '../lib/acta';
import ReciboCard from './ReciboCard';
import { construirHtmlRecibo } from '../lib/reciboHtml';

const fmt = v => new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(v || 0);
const hoy = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };

// items: [{ id, fecha, servicio, solicitante, valor, estado }]
export default function ModalRecibo({ cliente, items, onClose }) {
  const [fecha, setFecha] = useState(hoy());
  const [formaPago, setFormaPago] = useState('');
  const [observaciones, setObservaciones] = useState('');
  const [infoCliente, setInfoCliente] = useState({});
  const [generando, setGenerando] = useState(false);
  const [error, setError] = useState('');
  const [generado, setGenerado] = useState(null); // { numero, fecha, items, html }
  const [exportando, setExportando] = useState(false);

  useEffect(() => {
    let vivo = true;
    API.get(`/clientes/${encodeURIComponent(cliente)}/info`)
      .then(r => { if (vivo) setInfoCliente(r.data || {}); })
      .catch(() => {});
    return () => { vivo = false; };
  }, [cliente]);

  const total = items.reduce((s, i) => s + (Number(i.valor) || 0), 0);
  const pendientes = items.filter(i => i.estado !== 'PAGADO').length;

  async function generar(e) {
    e.preventDefault();
    if (generando) return;
    setGenerando(true); setError('');
    try {
      const { data } = await API.post('/recibos', {
        cliente, fecha, forma_pago: formaPago, observaciones: observaciones.trim(),
        registro_ids: items.map(i => i.id),
      });
      const html = construirHtmlRecibo({ cliente, infoCliente, items: data.items, fecha: data.fecha, numero: data.numero, formaPago, observaciones: observaciones.trim() });
      setGenerado({ numero: data.numero, fecha: data.fecha, items: data.items, html });
    } catch (err) {
      setError(err?.response?.data?.error || 'No se pudo generar el recibo');
    } finally { setGenerando(false); }
  }

  // Imagen JPG del recibo (para WhatsApp), igual que en las cotizaciones.
  async function capturar() {
    const el = document.getElementById('recibo-whatsapp-card');
    if (!el) throw new Error('sin tarjeta');
    return html2canvas(el, { backgroundColor: '#ffffff', scale: 2, useCORS: true });
  }
  async function descargarJpg() {
    setExportando(true);
    try {
      const canvas = await capturar();
      const a = document.createElement('a');
      a.href = canvas.toDataURL('image/jpeg', 0.94);
      a.download = `${generado.numero}-whatsapp.jpg`;
      document.body.appendChild(a); a.click(); a.remove();
    } catch (_) { alert('No se pudo generar la imagen del recibo.'); }
    finally { setExportando(false); }
  }
  // En el celular abre el menu de compartir (WhatsApp, etc.) con la imagen; si no hay, descarga el JPG.
  async function compartir() {
    setExportando(true);
    try {
      const canvas = await capturar();
      const blob = await new Promise(res => canvas.toBlob(res, 'image/jpeg', 0.94));
      const archivo = new File([blob], `${generado.numero}-whatsapp.jpg`, { type: 'image/jpeg' });
      if (navigator.canShare && navigator.canShare({ files: [archivo] })) {
        await navigator.share({ files: [archivo], title: `Recibo ${generado.numero}`, text: `Recibo ${generado.numero} · ${cliente}` });
      } else {
        await descargarJpg();
      }
    } catch (err) { if (err?.name !== 'AbortError') alert('No se pudo compartir el recibo.'); }
    finally { setExportando(false); }
  }
  const puedeCompartir = typeof navigator !== 'undefined' && typeof navigator.share === 'function' && typeof navigator.canShare === 'function';

  const etiqueta = { fontSize: 11, fontWeight: 600, color: 'var(--app-muted)', display: 'block', marginBottom: 4 };

  if (generado) {
    return (
      <div className="modal-overlay" style={{ zIndex: 1200 }}>
        <div className="modal" style={{ maxWidth: 480, padding: 18 }}>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#059669', marginBottom: 4 }}>Recibo generado y guardado</div>
          <div style={{ fontSize: 12, color: 'var(--app-muted)', marginBottom: 12 }}>Descarga la imagen para enviarla por WhatsApp o imprime el PDF. Para reenviarlo no se crea otro número.</div>
          <div style={{ maxHeight: '58vh', overflowY: 'auto', marginBottom: 12, border: '1px solid var(--app-border)', borderRadius: 4 }}>
            <ReciboCard numero={generado.numero} fecha={generado.fecha} cliente={cliente} infoCliente={infoCliente} items={generado.items} formaPago={formaPago} observaciones={observaciones.trim()} />
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button type="button" disabled={exportando} className="btn-ui btn-ui--md btn-ui--primary" onClick={descargarJpg}>{exportando ? 'Generando...' : 'Descargar JPG'}</button>
            {puedeCompartir && <button type="button" disabled={exportando} className="btn-ui btn-ui--md btn-ui--dark" onClick={compartir}>Compartir por WhatsApp</button>}
            <button type="button" className="btn-ui btn-ui--md btn-ui--outline" onClick={() => imprimirHTML(generado.html)}>Imprimir / PDF</button>
            <button type="button" className="btn-ui btn-ui--md btn-ui--outline" onClick={onClose}>Cerrar</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay" style={{ zIndex: 1200 }}>
      <form className="modal" onSubmit={generar} style={{ maxWidth: 520, padding: 22 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--app-heading)', marginBottom: 2 }}>Generar recibo</div>
        <div style={{ fontSize: 12, color: 'var(--app-muted)', marginBottom: 14 }}>{cliente} · {items.length} servicio{items.length === 1 ? '' : 's'} · <b style={{ color: 'var(--app-heading)' }}>{fmt(total)}</b></div>

        <div style={{ maxHeight: 150, overflowY: 'auto', border: '1px solid var(--app-border)', borderRadius: 2, marginBottom: 14 }}>
          {items.map((i, k) => (
            <div key={k} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, padding: '6px 10px', borderBottom: k < items.length - 1 ? '1px solid var(--app-border)' : 'none', fontSize: 12 }}>
              <span style={{ minWidth: 0 }}>{String(i.fecha || '').slice(0, 10)} · {i.servicio || '—'}</span>
              <span style={{ fontWeight: 700, whiteSpace: 'nowrap' }}>{fmt(i.valor)}</span>
            </div>
          ))}
        </div>
        {pendientes > 0 && (
          <div style={{ fontSize: 11, color: '#b45309', marginBottom: 12 }}>
            {pendientes} de los servicios seleccionados aparece{pendientes === 1 ? '' : 'n'} sin pagar; el recibo mostrará el saldo pendiente.
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
          <div><label style={etiqueta}>Fecha del recibo</label><input className="input" type="date" value={fecha} onChange={e => setFecha(e.target.value)} required /></div>
          <div>
            <label style={etiqueta}>Forma de pago (opcional)</label>
            <select className="input" value={formaPago} onChange={e => setFormaPago(e.target.value)}>
              <option value="">Sin especificar</option>
              <option>Efectivo</option><option>Transferencia</option><option>Tarjeta</option><option>Otro</option>
            </select>
          </div>
        </div>
        <div style={{ marginBottom: 14 }}>
          <label style={etiqueta}>Observaciones (opcional)</label>
          <textarea className="input" rows={2} value={observaciones} onChange={e => setObservaciones(e.target.value)} style={{ resize: 'vertical' }} />
        </div>
        <div style={{ fontSize: 11, color: 'var(--app-muted)', marginBottom: 14 }}>El número se asigna automáticamente (iniciales del cliente + consecutivo). El recibo no incluye IVA.</div>
        {error && <div style={{ fontSize: 12, color: '#dc2626', marginBottom: 10 }}>{error}</div>}

        <div style={{ display: 'flex', gap: 8 }}>
          <button type="submit" disabled={generando} className="btn-ui btn-ui--md btn-ui--primary">{generando ? 'Generando...' : 'Generar recibo'}</button>
          <button type="button" className="btn-ui btn-ui--md btn-ui--outline" onClick={onClose}>Cancelar</button>
        </div>
      </form>
    </div>
  );
}
